import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  MessageSquare,
  Search,
  Send,
  Trash2,
  Link2,
  ShieldCheck,
  ArrowBigUp,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";

const ADMIN_HANDLE = "WeLivdIt Team";

const TEMPLATES: { label: string; body: string }[] = [
  {
    label: "214b Strategy",
    body: "A 214(b) refusal is about non-immigrant intent, not paperwork. Rebuild the answer around one clear primary funding source, a specific career plan in India, and concrete home ties. Keep every answer under 20 seconds and consistent with your DS-160.",
  },
  {
    label: "SEVIS Fee Rules",
    body: "Pay the SEVIS I-901 fee after your biometrics appointment but at least 2–3 business days before the visa interview, and carry the printed receipt with your I-20.",
  },
  {
    label: "Loan Verification",
    body: "Carry the sanction letter, disbursement schedule, and collateral/property documents. Show total capability of 1.5x–2x your first-year I-20 estimate, with the loan named as the primary source.",
  },
];

type Filter = "all" | "unanswered" | "answered" | "resolved";

const PAGE_SIZE = 10;

type QRow = {
  id: string;
  page_slug: string;
  user_id: string;
  author_name: string | null;
  content: string;
  created_at: string;
  resolved_at: string | null;
};

function timeAgo(iso: string) {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} mins ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function CommunityQueriesWidget() {
  const [rows, setRows] = useState<QRow[]>([]);
  const [replyCounts, setReplyCounts] = useState<Record<string, number>>({});
  const [scores, setScores] = useState<Record<string, number>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [{ data: qs }, { data: rs }, { data: vs }] = await Promise.all([
      supabase
        .from("guide_questions")
        .select("id, page_slug, user_id, author_name, content, created_at, resolved_at")
        .order("created_at", { ascending: false })
        .limit(200),
      supabase.from("guide_question_replies").select("question_id").limit(2000),
      supabase.from("guide_question_votes").select("question_id, value").limit(5000),
    ]);
    const counts: Record<string, number> = {};
    for (const r of rs ?? []) counts[r.question_id] = (counts[r.question_id] ?? 0) + 1;
    const sc: Record<string, number> = {};
    for (const v of vs ?? []) sc[v.question_id] = (sc[v.question_id] ?? 0) + v.value;
    setRows((qs ?? []) as QRow[]);
    setReplyCounts(counts);
    setScores(sc);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
    void supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));

    const channel = supabase
      .channel("admin-community-queries")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "guide_questions" },
        (payload) => {
          setRows((prev) => [payload.new as QRow, ...prev.filter((r) => r.id !== (payload.new as QRow).id)]);
        },
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "guide_questions" },
        (payload) => {
          setRows((prev) => prev.filter((r) => r.id !== (payload.old as { id: string }).id));
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const answered = (replyCounts[r.id] ?? 0) > 0;
      if (filter === "resolved" && !r.resolved_at) return false;
      if (filter !== "resolved" && r.resolved_at) return false;
      if (filter === "unanswered" && answered) return false;
      if (filter === "answered" && !answered) return false;
      if (!q) return true;
      return (
        r.content.toLowerCase().includes(q) || (r.author_name ?? "").toLowerCase().includes(q)
      );
    });
  }, [rows, search, filter, replyCounts]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const visible = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const pending = rows.filter((r) => !r.resolved_at && (replyCounts[r.id] ?? 0) === 0).length;
  const resolvedCount = rows.filter((r) => r.resolved_at).length;

  const toggleResolved = async (r: QRow) => {
    if (!userId) return;
    const next = r.resolved_at ? null : new Date().toISOString();
    setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, resolved_at: next } : x)));
    const { error } = await supabase
      .from("guide_questions")
      .update({ resolved_at: next, resolved_by: next ? userId : null })
      .eq("id", r.id);
    if (error) {
      setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, resolved_at: r.resolved_at } : x)));
      toast.error("Couldn't update that query.");
      return;
    }
    toast.success(next ? "Marked as resolved." : "Reopened.");
  };


  const sendReply = async (qid: string) => {
    const content = (drafts[qid] ?? "").trim();
    if (!content || !userId) return;
    setBusy(qid);
    const { error } = await supabase.from("guide_question_replies").insert({
      question_id: qid,
      user_id: userId,
      author_name: ADMIN_HANDLE,
      content: content.slice(0, 4000),
      is_admin: true,
    });
    setBusy(null);
    if (error) {
      toast.error("Couldn't post the reply.");
      return;
    }
    setDrafts((d) => ({ ...d, [qid]: "" }));
    setReplyCounts((c) => ({ ...c, [qid]: (c[qid] ?? 0) + 1 }));
    toast.success("Reply published as WeLivdIt Team.");
  };

  const removeQuery = async (qid: string) => {
    if (!window.confirm("Delete this query permanently?")) return;
    const snap = rows;
    setRows((r) => r.filter((x) => x.id !== qid));
    const { error } = await supabase.from("guide_questions").delete().eq("id", qid);
    if (error) {
      setRows(snap);
      toast.error("Couldn't delete that query.");
    }
  };

  const copyLink = (r: QRow) => {
    const slug = r.page_slug === "fresher-guide" ? "f1-visa-guide" : r.page_slug;
    void navigator.clipboard.writeText(`https://welivdit.com/resources/${slug}#community`);
    toast.success("Link copied.");
  };

  return (
    <div className="rounded-2xl border border-border bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-border/60 p-4">
        <h3 className="flex items-center gap-2 text-sm font-bold">
          <MessageSquare className="h-4 w-4 text-primary" /> Community Visa Queries
          <span className="relative ml-1 flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
        </h3>
        {pending > 0 && (
          <span className="rounded-full bg-red-500/10 px-2.5 py-1 text-[11px] font-bold text-red-600 dark:text-red-300">
            🔴 {pending} Pending {pending === 1 ? "Query" : "Queries"}
          </span>
        )}
        <div className="relative ml-auto w-full sm:w-56">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search queries…"
            className="h-9 pl-9 text-xs"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 px-4 pt-3">
        {(
          [
            ["all", "Open Queries"],
            ["unanswered", "Unanswered Only"],
            ["answered", "Answered"],
            ["resolved", `Resolved (${resolvedCount})`],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setFilter(key as Filter);
              setPage(0);
            }}
            className={
              "rounded-full border px-3 py-1.5 text-[11px] font-semibold transition " +
              (filter === key
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-muted/30 text-muted-foreground hover:border-primary/50")
            }
          >
            {label}
          </button>
        ))}
      </div>

      <div className="max-h-[32rem] space-y-3 overflow-y-auto p-4">
        {loading && (
          <p className="inline-flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading queries…
          </p>
        )}
        {!loading && filtered.length === 0 && (
          <p className="py-6 text-center text-xs text-muted-foreground">No queries match this view.</p>
        )}
        {visible.map((r) => {
          const answered = (replyCounts[r.id] ?? 0) > 0;
          return (
            <div key={r.id} className="rounded-xl border border-border/60 bg-muted/20 p-3.5">
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                <span className="grid h-6 w-6 place-items-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">
                  {(r.author_name ?? "S").replace(/^@/, "").slice(0, 2).toUpperCase()}
                </span>
                <span className="font-bold text-foreground">
                  @{(r.author_name ?? "student").replace(/^@/, "")}
                </span>
                <span>· {timeAgo(r.created_at)}</span>
                <span className="inline-flex items-center gap-0.5">
                  <ArrowBigUp className="h-3.5 w-3.5" /> {scores[r.id] ?? 0}
                </span>
                <span className="rounded-full bg-muted px-2 py-0.5 font-medium">/{r.page_slug}</span>
                {r.resolved_at ? (
                  <span className="rounded-full bg-sky-500/10 px-2 py-0.5 font-bold text-sky-600 dark:text-sky-300">
                    Resolved
                  </span>
                ) : answered ? (
                  <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 font-bold text-emerald-600 dark:text-emerald-300">
                    Answered
                  </span>
                ) : (
                  <span className="rounded-full bg-red-500/10 px-2 py-0.5 font-bold text-red-600 dark:text-red-300">
                    Pending
                  </span>
                )}
              </div>

              <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground/90">
                {r.content}
              </p>

              <div className="mt-3 flex flex-wrap gap-2">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.label}
                    type="button"
                    onClick={() =>
                      setDrafts((d) => ({ ...d, [r.id]: `${(d[r.id] ?? "").trim()}\n${t.body}`.trim() }))
                    }
                    className="rounded-full border border-dashed border-primary/40 px-2.5 py-1 text-[11px] font-semibold text-primary hover:bg-primary/5"
                  >
                    + Insert Template: {t.label}
                  </button>
                ))}
              </div>

              <Textarea
                rows={2}
                value={drafts[r.id] ?? ""}
                onChange={(e) => setDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
                placeholder="Reply as WeLivdIt Team…"
                className="mt-2 resize-none text-xs"
              />

              <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                <Button size="sm" variant="ghost" className="gap-1.5 text-xs" onClick={() => copyLink(r)}>
                  <Link2 className="h-3.5 w-3.5" /> Copy link
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1.5 text-xs text-sky-600 hover:text-sky-600 dark:text-sky-300"
                  onClick={() => void toggleResolved(r)}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {r.resolved_at ? "Reopen" : "Mark resolved"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1.5 text-xs text-destructive hover:text-destructive"
                  onClick={() => removeQuery(r.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </Button>
                <Button
                  size="sm"
                  className="gap-1.5 text-xs"
                  disabled={busy === r.id || !(drafts[r.id] ?? "").trim()}
                  onClick={() => sendReply(r.id)}
                >
                  {busy === r.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Send className="h-3.5 w-3.5" />
                  )}
                  Reply
                </Button>
              </div>

              {answered && (
                <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-emerald-600 dark:text-emerald-300">
                  <ShieldCheck className="h-3.5 w-3.5" /> {replyCounts[r.id]} official{" "}
                  {replyCounts[r.id] === 1 ? "reply" : "replies"} published
                </p>
              )}
            </div>
          );
        })}
      </div>

      {filtered.length > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-3 border-t border-border/60 px-4 py-3 text-xs">
          <span className="text-muted-foreground">
            Showing {safePage * PAGE_SIZE + 1}–{Math.min(filtered.length, (safePage + 1) * PAGE_SIZE)} of{" "}
            {filtered.length}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              disabled={safePage === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </Button>
            <span className="text-muted-foreground">
              Page {safePage + 1} / {pageCount}
            </span>
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              disabled={safePage >= pageCount - 1}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

